/**
 * The pantry as a table (docs/18-vorratsbuch.md, Bestandsliste): the stock list to take away and
 * a plan of what an uploaded list would change, after KellerLog's rules for its wines
 * (catalog-import.ts). Same columns and headings as the Eingang's template (TABLE_COLUMNS), so the
 * template can be filled in here too. Nothing is written here; the app shows the plan and writes it
 * only after the person agreed.
 *
 * Rules:
 *   - A row is a pantry article in one storage with one best-before. It is recognised by its
 *     „Kennung“, else by Bezeichnung, Marke and Inhalt je Gebinde, so the same list can be
 *     uploaded again: known articles are changed, not doubled.
 *   - An empty cell leaves a field as it is. „Zählen in“ and „Inhalt je Gebinde“ of a known article
 *     stay, as in the form after the first booking.
 *   - Stock comes from the table only as opening stock, for articles never booked. After that only
 *     bookings change it; otherwise journal and inventory would no longer agree. Another quantity
 *     in the table gives a note.
 *   - The same article on several rows is one article; the stock of each row goes to its storage.
 *   - The stock list is not for the Eingang: there it would be stored again (`isStockList`).
 */
import type { Cell } from "./catalog-import.ts";
import type { Inbound, InboundLine } from "./inbound.ts";
import { TABLE_COLUMNS } from "./inbound-table.ts";
import { lineQty, newArticleFields } from "./inbound-match.ts";
import type { PantryArticle } from "./inbound-match.ts";
import { PANTRY_AISLES, baseProduct } from "./base-products.ts";
import { isPantry, pantryName, pantryQtyText, pantrySize } from "./pantry.ts";
import { profileById } from "./profiles.ts";
import type { State } from "./ledger.ts";
import { stockLines } from "./ledger.ts";
import { fold } from "./search.ts";

/** The sheet's name in the stock list; the template's is „Vorrat“. */
export const STOCK_SHEET = "Bestand";

/** Whether a workbook is the stock list from Vorräte, by its sheet's name. */
export const isStockList = (sheetNames: readonly string[]) => sheetNames.some((n) => fold(n) === fold(STOCK_SHEET));

const day = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
const round2 = (n: number) => Math.round(n * 100) / 100;

export interface TableLager {
  id: string;
  name: string;
}

/**
 * The stock list: headings, then a row per article, storage and best-before with its stock; an
 * article without stock on one row with an empty quantity. Articles in containers count in
 * containers („1,5“ Packung), the others in their unit. `prices`: the value of each row, if the
 * household records prices. Archived articles and drinks stay out.
 */
export function pantryStockTable(articles: readonly PantryArticle[], state: State, lagers: readonly TableLager[], prices = false): Cell[][] {
  const lagerName = new Map(lagers.map((l) => [l.id, l.name]));
  const order = new Map(lagers.map((l, i) => [l.id, i]));
  // Per article: storage × best-before → quantity and value.
  const held = new Map<string, Map<string, { lager: string; bestBefore: string; qty: number; value: number; unpriced: boolean }>>();
  for (const line of stockLines(state)) {
    if (line.qty <= 0 || !lagerName.has(line.lager)) continue;
    const bestBefore = (line.lot && state.lots.get(line.lot)?.bestBefore) || "";
    const k = `${line.lager}|${bestBefore}`;
    const per = held.get(line.article) ?? new Map();
    const x = per.get(k) ?? { lager: line.lager, bestBefore, qty: 0, value: 0, unpriced: false };
    x.qty += line.qty;
    x.value += line.value;
    x.unpriced ||= line.unpriced;
    per.set(k, x);
    held.set(line.article, per);
  }
  const rows: Cell[][] = [TABLE_COLUMNS.map((c) => c.label)];
  const shown = articles.filter((a) => isPantry(a.fields) && a.fields.archived !== true);
  // As the list of Vorräte: aisles in the order of the walk through the shop, then by name.
  const walk = (f: Record<string, unknown>) => {
    const i = PANTRY_AISLES.findIndex((x) => x.name === f.aisle);
    return i < 0 ? PANTRY_AISLES.length : i;
  };
  shown.sort((x, y) => walk(x.fields) - walk(y.fields) || pantryName(x.fields).localeCompare(pantryName(y.fields), "de"));
  for (const a of shown) {
    const f = a.fields;
    const parts = [...(held.get(a.id)?.values() ?? [])].sort((x, y) => (order.get(x.lager) ?? 0) - (order.get(y.lager) ?? 0) || x.bestBefore.localeCompare(y.bestBefore));
    for (const part of parts.length ? parts : [null]) rows.push(TABLE_COLUMNS.map((c) => cellOf(c.key, a, f, part, lagerName, prices)));
  }
  return rows;
}

function cellOf(
  col: string,
  a: PantryArticle,
  f: Record<string, unknown>,
  part: { lager: string; bestBefore: string; qty: number; value: number; unpriced: boolean } | null,
  lagerName: ReadonlyMap<string, string>,
  prices: boolean,
): Cell {
  const size = pantrySize(f);
  const unit = typeof f.unit === "string" && f.unit !== "" ? f.unit : "Stk.";
  const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : null);
  const number = (v: unknown) => (typeof v === "number" ? v : null);
  switch (col) {
    case "product":
      return str(f.product) ?? str(f.name);
    case "brand":
      return str(f.brand);
    case "base":
      return baseProduct(f.base)?.names[0] ?? null;
    case "qty":
      return part ? (size !== null ? round2(part.qty / size) : part.qty) : null;
    case "unit":
      return size !== null ? (str(f.container) ?? "Packung") : unit;
    case "size":
      return size;
    case "base_unit":
      return unit;
    case "bestBefore":
      return part?.bestBefore ? day(part.bestBefore) : null;
    case "place":
      return part ? (lagerName.get(part.lager) ?? null) : null;
    case "amount":
      return prices && part && !part.unpriced && part.value > 0 ? round2(part.value / 100) : null;
    case "barcode":
      return Array.isArray(f.barcodes) && typeof f.barcodes[0] === "string" ? f.barcodes[0] : null;
    case "organic":
      return f.organic === true ? "ja" : null;
    case "reorderAt":
      return number(f.reorderAt);
    case "fillTo":
      return number(f.fillTo);
    case "restockAuto":
      return f.restockAuto === true ? "ja" : null;
    case "aisle":
      return str(f.aisle);
    case "keep":
      return str(f.keep);
    case "note":
      return str(f.note);
    case "id":
      return a.id;
    default:
      return null;
  }
}

/** Opening stock of a row, for an article never booked. `lager` null: where the article belongs. */
export interface PlannedOpening {
  lager: string | null;
  qty: number;
  bestBefore?: string;
  amount?: number;
}

export interface PantryTableRow {
  article: string;
  /** New, changed, or as it is. */
  state: "new" | "changed" | "same";
  name: string;
  /** For a new article all its fields, for a known one only those that change. */
  fields: Record<string, unknown>;
  /** The labels of the fields that change, for the preview. */
  changed: string[];
  opening: PlannedOpening[];
  notes: string[];
}

export interface PantryTablePlan {
  rows: PantryTableRow[];
  /** Rows left out and other notes of the table, as „Zeile 4 (…): …“. */
  notes: string[];
}

/** The fields an upload may change on a known article, with the column's label. */
const CHANGEABLE: readonly [string, string][] = [
  ["product", "Bezeichnung"],
  ["brand", "Marke"],
  ["base", "Grundprodukt"],
  ["container", "Einheit"],
  ["organic", "Bio"],
  ["reorderAt", "Nachkaufen ab"],
  ["fillTo", "Auffüllen auf"],
  ["restockAuto", "Immer automatisch senden"],
  ["aisle", "Abteilung"],
  ["keep", "Lagerung"],
  ["note", "Notiz"],
];

/** The Bezeichnung as written, before a content in it was read off („Müllbeutel 35 l“): the text without the brand. */
const written = (line: InboundLine) => {
  const brand = typeof line.fields?.brand === "string" ? line.fields.brand : "";
  return brand && line.text.startsWith(`${brand} `) ? line.text.slice(brand.length + 1) : line.text;
};

const identity = (product: unknown, brand: unknown, size: number | null) => `${fold(String(product ?? ""))}|${fold(String(brand ?? ""))}|${size ?? ""}`;

/**
 * What the stock list `doc` (parsed with `parseInboundTable(rows, { stockList: true })`) would
 * change. `booked`: the articles with any booking, whose stock the table leaves alone. `newId`
 * makes the ids of new articles.
 */
export function planPantryTable(doc: Inbound, articles: readonly PantryArticle[], state: State, booked: ReadonlySet<string>, lagers: readonly TableLager[], newId: () => string): PantryTablePlan {
  const notes = [...doc.notes];
  const byId = new Map(articles.map((a) => [a.id, a]));
  const byIdentity = new Map<string, PantryArticle>();
  for (const a of articles) {
    if (!isPantry(a.fields) || a.fields.archived === true) continue;
    const k = identity(a.fields.product ?? a.fields.name, a.fields.brand, pantrySize(a.fields));
    if (!byIdentity.has(k)) byIdentity.set(k, a);
  }
  const lagerOf = new Map(lagers.map((l) => [fold(l.name), l.id]));
  const rows = new Map<string, PantryTableRow>();
  const current = new Map<string, number>();
  for (const line of stockLines(state)) current.set(line.article, (current.get(line.article) ?? 0) + line.qty);
  const inTable = new Map<string, number>();

  for (const line of doc.lines) {
    if (line.group === "Getränke") continue;
    const where = `Zeile ${line.line}`;
    const brand = line.fields?.brand;
    const size = line.packs !== null && line.content ? line.content.qty : null;
    const known = (line.article ? byId.get(line.article) : undefined) ?? byIdentity.get(identity(written(line), brand, size)) ?? byIdentity.get(identity(line.name, brand, size));
    if (line.article && !byId.has(line.article)) notes.push(`${where} („${line.text}“): Kennung unbekannt, nach dem Namen zugeordnet oder neu angelegt.`);
    let row: PantryTableRow;
    if (known) {
      row = rows.get(known.id) ?? knownRow(known, line);
    } else {
      // The same new article on several rows: once, by the identity of its first row.
      const k = `new|${identity(line.name, brand, size)}`;
      row = rows.get(k) ?? newRow(newId(), line);
      rows.set(k, row);
    }
    if (known) rows.set(known.id, row);
    const f = known ? { ...known.fields, ...row.fields } : row.fields;
    const qty = line.qty === 0 ? 0 : lineQty(line, f);
    if (line.qty > 0 && qty === null) row.notes.push(`${where}: Menge „${line.qty} ${line.unit}“ passt nicht zu ${typeof f.unit === "string" ? f.unit : "Stk."}, ausgelassen.`);
    if (!qty) continue;
    inTable.set(row.article, (inTable.get(row.article) ?? 0) + qty);
    if (known && booked.has(known.id)) continue;
    let lager: string | null = null;
    if (line.place) {
      lager = lagerOf.get(fold(line.place)) ?? null;
      if (!lager) row.notes.push(`${where}: Lager „${line.place}“ gibt es hier nicht; es kommt, wohin der Vorrat gehört.`);
    }
    row.opening.push({ lager, qty, ...(line.bestBefore ? { bestBefore: line.bestBefore } : {}), ...(line.amount !== undefined ? { amount: line.amount } : {}) });
  }

  for (const row of rows.values()) {
    // An empty quantity says nothing about the stock; only a number is compared.
    if (row.state === "new" || !booked.has(row.article) || !inTable.has(row.article)) continue;
    const f = byId.get(row.article)!.fields;
    const has = current.get(row.article) ?? 0;
    const said = inTable.get(row.article) ?? 0;
    // The list rounds containers to two places: a difference below that is the rounding.
    const slack = (pantrySize(f) ?? 1) * 0.01 + 0.5;
    if (Math.abs(said - has) > slack) row.notes.push(`Bestand laut Tabelle ${said > 0 ? pantryQtyText(said, f) : "leer"}, in VorratsBuch ${has > 0 ? pantryQtyText(has, f) : "leer"}: bleibt. Ändern mit Buchen oder der Inventur.`);
  }
  for (const row of rows.values()) if (row.state === "same" && row.opening.length) row.state = "changed";
  return { rows: [...rows.values()], notes };
}

function knownRow(a: PantryArticle, line: InboundLine): PantryTableRow {
  const f = a.fields;
  const P = profileById(typeof f.profile === "string" ? f.profile : undefined);
  const has = new Set(P.fields.map((x) => x.key));
  const given: Record<string, unknown> = { ...line.fields, product: written(line), ...(line.organic ? { organic: true } : {}) };
  const fields: Record<string, unknown> = {};
  const changed: string[] = [];
  for (const [k, label] of CHANGEABLE) {
    if (!has.has(k) || given[k] === undefined || given[k] === f[k]) continue;
    // A container only matters for articles in containers; „Packung“ is the default.
    if (k === "container" && (pantrySize(f) === null || (given[k] === "Packung" && f[k] === undefined))) continue;
    // Matched by name, the product is the same; only a Kennung can rename it.
    if (k === "product" && line.article !== a.id) continue;
    fields[k] = given[k];
    if (k === "aisle") fields.category = given[k];
    changed.push(label);
  }
  const barcodes = Array.isArray(f.barcodes) ? (f.barcodes as string[]) : [];
  const added = (Array.isArray(given.barcodes) ? (given.barcodes as string[]) : []).filter((b) => !barcodes.includes(b));
  if (added.length) {
    fields.barcodes = [...barcodes, ...added];
    changed.push("Strichcode");
  }
  const notes: string[] = [];
  const unit = typeof f.unit === "string" ? f.unit : "Stk.";
  const lineUnit = line.total?.unit;
  if (lineUnit && line.qty > 0 && lineUnit !== unit) notes.push(`Zählen in „${lineUnit}“: bleibt ${unit}.`);
  const size = line.packs !== null && line.content ? line.content.qty : null;
  if (size !== null && pantrySize(f) !== null && size !== pantrySize(f) && line.article === a.id) notes.push(`Inhalt je Gebinde ${size}: bleibt ${pantrySize(f)} ${unit}.`);
  if (changed.length) fields.name = pantryName({ ...f, ...fields });
  return { article: a.id, state: changed.length ? "changed" : "same", name: pantryName({ ...f, ...fields }), fields, changed, opening: [], notes };
}

function newRow(id: string, line: InboundLine): PantryTableRow {
  const base = typeof line.fields?.base === "string" ? baseProduct(line.fields.base) : undefined;
  const fields = newArticleFields(line, base);
  // Only the profile's own fields: no „Bio“ on a household article.
  const P = profileById(String(fields.profile));
  const own = new Set([...P.fields.map((x) => x.key), "profile", "name", "tracking", "category"]);
  for (const k of Object.keys(fields)) if (!own.has(k)) delete fields[k];
  if (fields.organic === false) delete fields.organic;
  return { article: id, state: "new", name: String(fields.name), fields, changed: [], opening: [], notes: [] };
}
